/**
 * Mantiene la pantalla encendida durante el entreno.
 *
 * Estrategia en dos pasos:
 *  1. Screen Wake Lock API, la vía oficial (Chrome Android, Safari 16.4+).
 *  2. Si no existe, un vídeo mudo de 64x64 en bucle fuera de pantalla: los
 *     navegadores no apagan la pantalla mientras se reproduce vídeo. Es el
 *     único recurso que funciona en iOS antiguo.
 *
 * El bloqueo se pierde al minimizar la app, así que se recupera solo cuando
 * la pestaña vuelve a estar visible.
 */

export type AwakeMode = 'native' | 'video' | 'none'

const VIDEO_SRC = '/no-sleep.mp4'

class ScreenAwake {
  #sentinel: WakeLockSentinel | null = null
  #video: HTMLVideoElement | null = null
  #wanted = false
  #mode: AwakeMode = 'none'
  #listening = false

  get mode(): AwakeMode {
    return this.#mode
  }

  get active(): boolean {
    return this.#wanted && this.#mode !== 'none'
  }

  /** Pide mantener la pantalla encendida. Llamar dentro de un gesto del usuario. */
  async enable(): Promise<AwakeMode> {
    this.#wanted = true
    this.#listen()
    return this.#acquire()
  }

  async disable(): Promise<void> {
    this.#wanted = false
    this.#mode = 'none'

    const sentinel = this.#sentinel
    this.#sentinel = null
    if (sentinel) {
      try {
        await sentinel.release()
      } catch {
        // Ya liberado por el sistema.
      }
    }

    if (this.#video) {
      this.#video.pause()
      this.#video.remove()
      this.#video = null
    }
  }

  async #acquire(): Promise<AwakeMode> {
    if (!this.#wanted) return 'none'

    if ('wakeLock' in navigator) {
      try {
        this.#sentinel = await navigator.wakeLock.request('screen')
        this.#sentinel.addEventListener('release', () => {
          this.#sentinel = null
        })
        this.#mode = 'native'
        return 'native'
      } catch {
        // Permiso denegado o pestaña oculta: se prueba con el vídeo.
      }
    }

    return this.#playVideo()
  }

  async #playVideo(): Promise<AwakeMode> {
    try {
      const video = this.#video ?? this.#createVideo()
      this.#video = video
      await video.play()
      this.#mode = 'video'
      return 'video'
    } catch {
      this.#mode = 'none'
      return 'none'
    }
  }

  #createVideo(): HTMLVideoElement {
    const video = document.createElement('video')
    video.src = VIDEO_SRC
    video.loop = true
    video.muted = true
    video.defaultMuted = true
    video.playsInline = true
    video.setAttribute('playsinline', '')
    video.setAttribute('aria-hidden', 'true')
    video.style.cssText =
      'position:fixed;width:1px;height:1px;opacity:0.01;pointer-events:none;bottom:0;left:0;z-index:-1'
    document.body.append(video)
    return video
  }

  #listen(): void {
    if (this.#listening) return
    this.#listening = true

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.#wanted && !this.#sentinel) {
        void this.#acquire()
      }
    })
  }
}

export const screenAwake = new ScreenAwake()
